import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import campaignService, { CampaignCostReportJob } from '../../services/campaignService';

const ACTIVE_STATUSES = new Set(['queued', 'running']);

const errorMessage = (error: any): string =>
  error?.response?.data?.detail || error?.message || 'No fue posible completar la operación.';

const statusLabel = (status: string): string => ({
  queued: 'En cola',
  running: 'Generando',
  completed: 'Disponible',
  failed: 'Fallido',
  expired: 'Expirado',
}[status] || status);

const statusColor = (status: string): 'default' | 'info' | 'success' | 'error' | 'warning' => ({
  queued: 'info',
  running: 'info',
  completed: 'success',
  failed: 'error',
  expired: 'warning',
}[status] as any || 'default');

const formatUsd = (value: string | number): string =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(Number(value || 0));

const CampaignCostReports: React.FC = () => {
  const [reports, setReports] = useState<CampaignCostReportJob[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const loadReports = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await campaignService.listCostReports({ limit: 50 });
      setReports(response.reports || []);
      setError(null);
    } catch (loadError) {
      if (!quiet) setError(errorMessage(loadError));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const hasActiveReports = useMemo(
    () => reports.some((report) => ACTIVE_STATUSES.has(report.status)),
    [reports]
  );

  useEffect(() => {
    if (!hasActiveReports) return undefined;
    const timer = window.setInterval(() => void loadReports(true), 4000);
    return () => window.clearInterval(timer);
  }, [hasActiveReports, loadReports]);

  const createReport = async () => {
    if (startDate && endDate && startDate > endDate) {
      setError('La fecha inicial debe ser anterior o igual a la fecha final.');
      return;
    }
    setCreating(true);
    setError(null);
    setSuccess(null);
    try {
      const report = await campaignService.createCostReport({
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
      });
      setReports((current) => [report, ...current.filter((item) => item.id !== report.id)]);
      setSuccess('El reporte quedó en cola. Esta página se actualizará automáticamente.');
    } catch (createError) {
      setError(errorMessage(createError));
    } finally {
      setCreating(false);
    }
  };

  const downloadReport = async (report: CampaignCostReportJob) => {
    setDownloadingId(report.id);
    setError(null);
    try {
      const blob = await campaignService.downloadCostReport(report.id);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `reporte_costos_campanas_${report.start_date || 'inicio'}_${report.end_date || 'actual'}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setError(errorMessage(downloadError));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Box className="fade-in">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Reportes de costos de campañas
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Genera el soporte de tesorería por proyecto, fecha, campaña y plantilla entregada.
      </Typography>

      <Stack spacing={2.5}>
        <Paper sx={{ p: 2.5 }}>
          <Typography variant="h6" gutterBottom>Nuevo reporte</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Fecha inicial (opcional)"
              type="date"
              size="small"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Fecha final (opcional)"
              type="date"
              size="small"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="contained" onClick={() => void createReport()} disabled={creating}>
              {creating ? 'Encolando...' : 'Generar reporte completo'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Sin fechas: todo el historial hasta el momento de generación (America/Bogota).
            </Typography>
          </Stack>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        <Paper sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Reportes generados</Typography>
            <Button startIcon={<RefreshIcon />} onClick={() => void loadReports()} disabled={loading}>
              Actualizar
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Creado</TableCell>
                    <TableCell>Periodo</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Costo Meta</TableCell>
                    <TableCell align="right">Estimado</TableCell>
                    <TableCell align="right">Sin tarifa</TableCell>
                    <TableCell>Observaciones</TableCell>
                    <TableCell align="right">Archivo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center">Aún no hay reportes.</TableCell></TableRow>
                  )}
                  {reports.map((report) => {
                    const estimated = Number(report.fallback_cost || 0) > 0;
                    const provisional = Boolean(report.source_metadata_json?.provisional_current_day);
                    return (
                      <TableRow key={report.id}>
                        <TableCell>{new Date(report.created_at).toLocaleString('es-CO')}</TableCell>
                        <TableCell>{report.start_date || 'Inicio'} — {report.end_date || 'Actual'}</TableCell>
                        <TableCell><Chip size="small" color={statusColor(report.status)} label={statusLabel(report.status)} /></TableCell>
                        <TableCell align="right">{formatUsd(report.meta_cost)}</TableCell>
                        <TableCell align="right">{formatUsd(report.fallback_cost)}</TableCell>
                        <TableCell align="right">{report.unpriced_count}</TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            {estimated && <Chip size="small" color="warning" label="Contiene estimaciones" />}
                            {provisional && <Chip size="small" color="info" label="Día actual provisional" />}
                            {(report.warnings_json || []).map((warning, index) => (
                              <Typography key={index} variant="caption" color="warning.main">{warning}</Typography>
                            ))}
                            {report.error_message && <Typography variant="caption" color="error">{report.error_message}</Typography>}
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={downloadingId === report.id ? <CircularProgress size={14} /> : <DownloadIcon />}
                            disabled={report.status !== 'completed' || downloadingId === report.id}
                            onClick={() => void downloadReport(report)}
                          >
                            Descargar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default CampaignCostReports;
